import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import { CRUISE_TYPE, AIR_TYPE, HOTEL_TYPE, ONEWAY, OPENJAW, PACKAGE_TYPE, ROUNDTRIP, TOUR_TYPE } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import i18n, { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewComposable } from '@/views/composable';
import { useCheckMaxPassengersComposable } from './useCheckMaxPassengersComposable';

const currentLanguage = getCurrentLocale();
export function useBookComposable() {
  const gType = [
    { name: 'Leisure', code: 'LE' },
    { name: 'Wedding', code: 'WE' },
    { name: 'Incentive / Corporate', code: 'IN' },
    { name: 'Student', code: 'ST' },
    { name: 'Speculative Group', code: 'SP' },
    { name: 'Cruise', code: 'CR' },
    { name: 'Sports Group', code: 'SG' },
    { name: 'Other', code: 'OT' },
    { name: 'Air Only', code: 'NT' }
  ];

  const roomRequestSelections = [
    { code: '2DBL', name: i18n.global.t('book_form.2dbl') },
    { code: '2TWN', name: i18n.global.t('book_form.2twn') },
    { code: 'ADJR', name: i18n.global.t('book_form.adjr') },
    { code: 'ANNI', name: i18n.global.t('book_form.anni') },
    { code: 'ASCV', name: i18n.global.t('book_form.ascv') },
    { code: 'BALC', name: i18n.global.t('book_form.balc') },
    { code: 'BEAC', name: i18n.global.t('book_form.beac') },
    { code: 'CONR', name: i18n.global.t('book_form.conr') },
    { code: 'CRIB', name: i18n.global.t('book_form.crib') },
    { code: 'GDVW', name: i18n.global.t('book_form.gdvw') },
    { code: 'GRND', name: i18n.global.t('book_form.grnd') },
    { code: 'HIGH', name: i18n.global.t('book_form.high') },
    { code: 'HONY', name: i18n.global.t('book_form.hony') },
    { code: 'KING', name: i18n.global.t('book_form.king') },
    { code: 'LOWR', name: i18n.global.t('book_form.lowr') },
    { code: 'NSMK', name: i18n.global.t('book_form.nsmk') },
    { code: 'OCVW', name: i18n.global.t('book_form.ocvw') },
    { code: 'POOL', name: i18n.global.t('book_form.pool') },
    { code: 'QUEE', name: i18n.global.t('book_form.quee') },
    { code: 'QUIE', name: i18n.global.t('book_form.quie') },
    { code: 'REPT', name: i18n.global.t('book_form.rept') },
    { code: 'SENI', name: i18n.global.t('book_form.seni') },
    { code: 'SMKG', name: i18n.global.t('book_form.smkg') }
  ];

  const columns = [
    { label: 'book_form.single', name: 'Single', field: 'single', peopleNo: 1 },
    { label: 'book_form.double', name: 'Double', field: 'double', peopleNo: 2 },
    { label: 'book_form.triple', name: 'Triple', field: 'triple', peopleNo: 3 },
    { label: 'book_form.quad', name: 'Quad', field: 'quad', peopleNo: 4 },
    { label: 'book_form.five', name: 'Five', field: 'fiveInRoom', peopleNo: 5 },
    { label: 'book_form.six', name: 'Six', field: 'sixInRoom', peopleNo: 6 }
  ];

  const findGroupType = (code) => {
    return gType.find((item) => item.code === code);
  };
  const checkInputAlphanumeric = (target, field) => {
    const regex = /^[a-zA-Z0-9 ]{0,40}$/;
    if (!regex.test(target[field])) {
      target[field] = target[field].replace(/[^a-zA-Z0-9 ]/g, '');
    }
  };
  const checkInputAlpha = (target, field) => {
    const regex = /^[a-zA-Z ]{0,40}$/;
    if (!regex.test(target[field])) {
      target[field] = target[field].replace(/[^a-zA-Z ]/g, '');
    }
  };
  const checkInputEmail = (target, field) => {
    const regex = /^[a-zA-Z0-9@. ]{0,40}$/;
    if (!regex.test(target[field])) {
      target[field] = target[field].replace(/[^a-zA-Z0-9@. ]/g, '');
    }
  };
  const getAge = (birthDate, targetDate) => {
    if (birthDate) {
      const newBirthDate = DateTime.fromJSDate(new Date(birthDate));
      const newReDate = DateTime.fromJSDate(new Date(targetDate || new Date()));
      const utcBirthDate = newBirthDate.setZone('utc', { keepLocalTime: true });
      const utcReDate = newReDate.setZone('utc', { keepLocalTime: true });
      return Math.floor(utcReDate.diff(utcBirthDate, 'years').years);
    }
    return 0;
  };

  const checkPriorityStatus = (priorityStatus, isManagement) => {
    const { statusPriorityNumber } = useViewComposable();
    return !isManagement && (priorityStatus > statusPriorityNumber.FOUR || priorityStatus === statusPriorityNumber.ZERO);
  };
  const roomFields = ['singleRoom', 'doubleRoom', 'tripleRoom', 'quadRoom', 'fiveInRoom', 'sixInRoom'];
  const userAuthorizationsStore = useUserAuthorizationsStore();
  const authorizations: any = userAuthorizationsStore.permissionByRole;
  const hasEditOccupacyPermission = authorizations?.my_quote?.save_occupancy_rooming_list_edit;
  const hasViewOccupacyPermission = authorizations?.my_quote?.save_occupancy_rooming_list_view;
  const hasEditBookPermission = authorizations?.my_quote?.book_edit;

  const columnStyles = [
    { wch: 5 },
    { wch: 10 },
    { wch: 30 },
    { wch: 10 },
    { wch: 20 },
    { wch: 25 },
    { wch: 10 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 15 },
    { wch: 10 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 30 },
    { wch: 30 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 },
    { wch: 15 }
  ];

  const passengerInfo = {
    age: 0,
    lastName: '',
    firstName: '',
    middleName: '',
    title: '',
    dateOfBirth: null,
    telephone: '',
    email: '',
    roomRequests: [],
    aeroPlan: '',
    seatingPreference: '',
    passport: {
      passportNumber: '',
      nationality: '',
      expiryDate: null,
      countryOfIssue: ''
    }
  };

  const initAdultPassengers = (numberAdult: number) => {
    const adults = [];
    for (let i = 0; i < numberAdult; i++) {
      const cloneInfo = cloneDeep(passengerInfo);
      adults.push({ ...cloneInfo });
    }
    return adults;
  };

  const formatInfants = (infantsData: any, cIndex: number, roomIndex: number) => {
    if (infantsData) {
      return infantsData.map((infant: any, index: number) => {
        return {
          ...infant,
          key: `infant${cIndex}${roomIndex}${index}`
        };
      });
    }
    return [];
  };

  const formatChildren = (childrenData: any, cIndex: number, roomIndex: number) => {
    if (childrenData) {
      return childrenData.map((children: any, index: number) => {
        return {
          ...children,
          key: `children${cIndex}${roomIndex}${index}`
        };
      });
    }
    return [];
  };

  const handleFormatPassenger = (data: any, column: any, colIndex: number) => {
    const passengersData = [];
    for (let i = 0; i < data[`${column.field}No`]; i++) {
      const roomList = data[`${column.field}RoomInfos`] && cloneDeep(data[`${column.field}RoomInfos`][i]);
      if (roomList) {
        passengersData.push({
          key: `passengers${colIndex}${i}`,
          people: roomList.adtPassengers,
          children: formatChildren(roomList.chdPassengers, colIndex, i),
          infants: formatInfants(roomList.infPassengers, colIndex, i),
          email: roomList?.email,
          roomRequests: roomList?.roomRequests
        });
      } else {
        passengersData.push({
          key: `passengers${colIndex}${i}`,
          people: initAdultPassengers(column.peopleNo),
          children: [],
          infants: [],
          email: null,
          roomRequests: []
        });
      }
    }
    return passengersData;
  };

  const initOccupancyCabin = (cabin: any) => {
    const occupancy: any = [];
    if (!cabin) {
      return;
    }
    columns.forEach((column, columnIndex) => {
      if (cabin[`${column.field}No`]) {
        const passengers = handleFormatPassenger(cabin, column, columnIndex);
        occupancy.push({ label: column.label, field: column.field, numberOfPeople: column.peopleNo, passengers });
      }
    });

    return occupancy;
  };

  const initOccupancy = (room: any) => {
    const occupancy: any = [];
    const roomInfoData = cloneDeep(room);

    if (roomInfoData?.roomInfo) {
      columns.forEach((column, columnIndex) => {
        const numberPeople = pick(room, ['maxPaxAdult', 'maxPaxChild', 'maxPaxRoom', 'minAdPerChild', 'minPaxAdult', 'minPaxChild', 'minPaxRoom']);
        if (roomInfoData.roomInfo?.[`${column.field}No`]) {
          const passengers = handleFormatPassenger(roomInfoData.roomInfo, column, columnIndex);
          occupancy.push({ ...numberPeople, label: column.label, field: column.field, passengers });
        }
      });
    }

    return occupancy;
  };

  const buildDataArray = (data: any, retDate: string, type?: string, isManagement?: boolean): string[] => {
    const getSafe = (value: any) => value ?? '';

    const fullName = `${data.lastName ?? ''} ${data.firstName ?? ''} ${data.middleName ?? ''}`.trim();
    const dobFormatted = data.dateOfBirth ? DateTime.fromJSDate(new Date(data.dateOfBirth)).toFormat('LL/dd/yyyy') : '';
    const age = getAge(data.dateOfBirth, retDate);
    const expiryFormatted = data.passport?.expiryDate ? DateTime.fromJSDate(new Date(data.passport.expiryDate)).toFormat('LL/dd/yyyy') : '';

    const commonFields = [
      getSafe(data.isLead ? 'X' : ''),
      getSafe(data.leadEmail),
      getSafe(data.type),
      getSafe(data.lastName),
      getSafe(data.firstName),
      getSafe(data.middleName),
      getSafe(data.title),
      getSafe(dobFormatted),
      getSafe(age),
      getSafe(data.telephone),
      getSafe(data.aeroPlan),
      getSafe(data.seatingPreference)
    ];

    const categoryCode = isManagement ? [getSafe(data.roomCode)] : [];

    const passportFields = [getSafe(fullName), getSafe(data.passport?.passportNumber), getSafe(data.passport?.nationality), getSafe(expiryFormatted), getSafe(data.passport?.countryOfIssue)];
    if (type === AIR_TYPE) {
      return [getSafe(data.party), getSafe(data.class), ...commonFields, ...passportFields];
    }
    if (type === CRUISE_TYPE) {
      return [getSafe(data.room), getSafe(data.occLabel), getSafe(data.cabinCategory), ...commonFields, getSafe(convertRoomRequestsToString(data.roomRequests)), ...passportFields];
    }
    return [getSafe(data.room), getSafe(data.occLabel), getSafe(data.roomCategory), ...categoryCode, ...commonFields, getSafe(convertRoomRequestsToString(data.roomRequests)), ...passportFields];
  };

  const handleHeaderExport = (type: string = PACKAGE_TYPE, headerLead?: string, isManagement?: boolean) => {
    const commonFields = [
      i18n.global.t('book_form.lead_name_email'),
      i18n.global.t('worksheet.type'),
      i18n.global.t('book_form.last_name'),
      i18n.global.t('book_form.first_name'),
      i18n.global.t('book_form.middle_name'),
      i18n.global.t('book_form.title'),
      i18n.global.t('book_form.date_of_birth'),
      i18n.global.t('book_form.age'),
      i18n.global.t('book_form.telephone'),
      i18n.global.t('book_form.aeroplan'),
      i18n.global.t('book_form.seating_preference'),
      i18n.global.t('book_form.full_name'),
      i18n.global.t('book_form.passport_number'),
      i18n.global.t('book_form.nationality_from'),
      i18n.global.t('book_form.expiry_date'),
      i18n.global.t('book_form.country_of_issue')
    ];

    const categoryCode = isManagement ? [i18n.global.t('common.category')] : [];

    const headerMap: Record<string, string[]> = {
      [AIR_TYPE]: [i18n.global.t('book_form.party'), i18n.global.t('common.class'), `${headerLead}`, ...commonFields],
      [CRUISE_TYPE]: [
        i18n.global.t('book_form.room'),
        i18n.global.t('book_form.occupancy'),
        ...categoryCode,
        `${headerLead}`,
        ...commonFields.slice(0, 11),
        i18n.global.t('book_form.room_requests'),
        ...commonFields.slice(11, 14),
        ...commonFields.slice(14)
      ],
      default: [
        i18n.global.t('book_form.room'),
        i18n.global.t('book_form.occupancy'),
        i18n.global.t('book_form.category_name'),
        ...categoryCode,
        `${headerLead}`,
        ...commonFields.slice(0, 11),
        i18n.global.t('book_form.room_requests'),
        ...commonFields.slice(11, 14),
        ...commonFields.slice(14)
      ]
    };

    return headerMap[type] || [...headerMap.default];
  };

  const handleMergeRule = (roomData: any, header: any, startRow: number, endRow: number, type: string, isManagement?: boolean) => {
    const mergeRules: any = [];

    if (endRow > startRow) {
      mergeRules.push({ s: { r: startRow, c: 2 }, e: { r: endRow, c: 2 } });
      if (type !== CRUISE_TYPE && isManagement) {
        mergeRules.push({ s: { r: startRow, c: 3 }, e: { r: endRow, c: 3 } });
      }
    }

    let occStart = startRow;
    const leadEmailIndex = header?.indexOf(i18n.global.t('book_form.lead_name_email'));
    for (let i = 1; i < roomData.length; i++) {
      const prev = roomData[i - 1];
      const current = roomData[i];

      const isSameOccLabel = prev[1] === current[1];
      const isSameRoom = type !== CRUISE_TYPE ? prev[3] === current[3] : prev[2] === current[2];

      if (!isSameOccLabel || !isSameRoom) {
        if (occStart < startRow + i - 1) {
          mergeRules.push({ s: { r: occStart, c: 0 }, e: { r: startRow + i - 1, c: 0 } }, { s: { r: occStart, c: 1 }, e: { r: startRow + i - 1, c: 1 } }, { s: { r: occStart, c: leadEmailIndex }, e: { r: startRow + i - 1, c: leadEmailIndex } });
        }
        occStart = startRow + i;
      }
    }

    if (occStart < endRow) {
      mergeRules.push({ s: { r: occStart, c: 0 }, e: { r: endRow, c: 0 } });
      mergeRules.push({ s: { r: occStart, c: 1 }, e: { r: endRow, c: 1 } });
      mergeRules.push({ s: { r: occStart, c: leadEmailIndex }, e: { r: endRow, c: leadEmailIndex } });
    }

    return mergeRules;
  };

  const handleOccupancyInfoCabin = (occupancy: any, data: any, index: number, type: string, getNextRoom: any) => {
    const cabinData: any = [];
    const cabinDetail = data.cabins[index];

    occupancy.forEach((occupancyDetail: any) => {
      const indexRoom = getNextRoom();
      occupancyDetail.passengers.forEach((passenger: any) => {
        const sharedFields = {
          room: indexRoom,
          occLabel: i18n.global.t(occupancyDetail.label),
          leadEmail: passenger.email,
          cabinCategory: `${getTextVariant(currentLanguage, cabinDetail?.cabin, cabinDetail?.cabinFr)} - ${cabinDetail?.code}`
        };
        const adults = buildPersonList(passenger.people, 'common.adult', sharedFields, data.retDate, passenger.roomRequests, type);
        const children = buildPersonList(passenger.children, 'common.child', sharedFields, data.retDate, undefined, type);
        const infants = buildPersonList(passenger.infants, 'common.infant', sharedFields, data.retDate, undefined, type);
        cabinData.push(...adults, ...children, ...infants);
      });
    });
    return cabinData;
  };

  const handleOccupancyInfo = (occupancy: any, data: any, room: any, index: number, type: string, isManagement?: boolean, getNextRoom?: any) => {
    const roomData: any = [];

    occupancy.forEach((occupancyDetail: any) => {
      const indexRoom = getNextRoom();
      occupancyDetail.passengers.forEach((passenger: any) => {
        const sharedFields = {
          room: indexRoom,
          occLabel: i18n.global.t(occupancyDetail.label),
          roomCategory: getTextVariant(currentLanguage, room.longName, room.longNameFr),
          roomCode: room.code,
          leadEmail: passenger.email
        };

        const adults = buildPersonList(passenger.people, 'common.adult', sharedFields, data.retDate, passenger.roomRequests, type, isManagement);
        const children = buildPersonList(passenger.children, 'common.child', sharedFields, data.retDate, undefined, type, isManagement);
        const infants = buildPersonList(passenger.infants, 'common.infant', sharedFields, data.retDate, undefined, type, isManagement);

        roomData.push(...adults, ...children, ...infants);
      });
    });

    return roomData;
  };

  const buildPersonList = (list: any[], typeKey: string, sharedFields: any, retDate: string, roomRequests?: any, type?: string, isManagement?: boolean) => {
    return list.map((person) =>
      buildDataArray(
        {
          ...sharedFields,
          type: i18n.global.t(typeKey),
          ...person,
          ...(roomRequests ? { roomRequests } : {})
        },
        retDate,
        type,
        isManagement
      )
    );
  };

  const formatLayoutExport = (data: any, type: string, keyDetail: string, headerLead?: string, isManagement?: boolean) => {
    const dataFormat = type === AIR_TYPE || type === CRUISE_TYPE ? [data] : formatHotelData(data, keyDetail, type);
    const header = handleHeaderExport(type, headerLead, isManagement);
    const mergeRules: any = [];

    const result = dataFormat.reduce((total: any, item: any) => {
      const hotelInfo = buildHotelInfo(item, type);
      const { occupancyTemp, hotelMergeRules } = buildOccupancyData(item, data, header, total.length, type, keyDetail, isManagement);

      mergeRules.push(...hotelMergeRules);
      return [...total, hotelInfo, header, ...occupancyTemp, ['']];
    }, []);

    return {
      data: result,
      mergeRules: mergeRules
    };
  };

  const formatHotelData = (data: any, keyDetail: string, type: string) => {
    return data[keyDetail].map((item: any) => ({
      gateway: data.gateCode,
      destination: data.airportCode,
      hotelName: item.hotel,
      depDate: type === TOUR_TYPE ? item.depDate : data.depDate,
      retDate: data?.retDate,
      rooms: item.rooms,
      tourName: getTextVariant(currentLanguage, item.tourNameEn, item.tourNameFr)
    }));
  };

  const buildHotelInfo = (item: any, type: string) => {
    const depDate = formatDate(item.depDate, 'dd-LLL-yyyy', true, currentLanguage);
    const retDate = formatDate(item.retDate, 'dd-LLL-yyyy', true, currentLanguage);

    const getAirHeaderInfo = () => {
      switch (item.itinerary) {
        case ROUNDTRIP:
          return `${item.gateCode}-${item.destCode} ${i18n.global.t('advance_quote.form.round_trip').toUpperCase()} | ${depDate} - ${retDate}`;
        case OPENJAW:
          return `${item.gateCode}-${item.destCode}/${item.returnFromCode}-${item.returnToCode} ${i18n.global.t('advance_quote.form.open_jaw').toUpperCase()} | ${depDate} - ${retDate}`;
        case ONEWAY:
          return `${item.gateCode}-${item.destCode} ${i18n.global.t('advance_quote.form.one_way').toUpperCase()} | ${depDate}`;
      }
    };

    switch (type) {
      case HOTEL_TYPE:
        return [`${item.hotelName} | ${depDate} - ${retDate}`];
      case TOUR_TYPE:
        return [`${item.gateway} - ${item.destination} | ${item.tourName} | ${depDate}`];
      case AIR_TYPE:
        return [getAirHeaderInfo()];
      case CRUISE_TYPE:
        return [`${item.cruiseLine} | ${item.ship} | ${item.sailing}`];
      default:
        return [`${item.gateway} - ${item.destination} | ${item.hotelName} | ${depDate}`];
    }
  };

  const getAirPaxListDataXlsx = (pricesData: any, retDate: any) => {
    const result: any[] = [];
    const mergeRules: any[] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: 4 }
      }
    ];
    let totalPassengers = 0;
    const rowOffset = 2;

    let partyIndex = 1;
    pricesData.forEach((price: any) => {
      const priceClass = (price.fareBasis?.split(' / ') ?? []).map((part: string) => part.charAt(0)).join('/');

      price.parties?.forEach((party: any) => {
        const sharedFields = {
          party: partyIndex,
          leadEmail: party.email,
          class: priceClass
        };

        const adults = [];
        const children = [];
        const infants = [];
        if (price.numberOfSeats || price.adtNo || price.chdNo) {
          adults.push(...buildPersonList(party.adtPassengers ?? [], 'common.adult', sharedFields, retDate, null, AIR_TYPE));
          children.push(...buildPersonList(party.chdPassengers ?? [], 'common.child', sharedFields, retDate, null, AIR_TYPE));
          infants.push(...buildPersonList(party.infPassengers ?? [], 'common.infant', sharedFields, retDate, null, AIR_TYPE));
          const partyLength = adults.length + children.length + infants.length;

          mergeRules.push({
            s: { r: totalPassengers + rowOffset, c: 0 },
            e: { r: totalPassengers + partyLength + rowOffset - 1, c: 0 }
          });
          mergeRules.push({
            s: { r: totalPassengers + rowOffset, c: 3 },
            e: { r: totalPassengers + partyLength + rowOffset - 1, c: 3 }
          });

          totalPassengers += partyLength;

          if (partyLength) {
            partyIndex++;
          }

          result.push(...adults, ...children, ...infants);
        }
      });
    });

    return {
      data: result,
      mergeRulesData: mergeRules
    };
  };

  const buildOccupancyData = (item: any, data: any, header: any, offset: number, type: string, keyDetail: string, isManagement?: boolean) => {
    const occupancyTemp: any = [];
    const mergeRules: any = [];

    const createRoomIndexGenerator = () => {
      let current = 1;
      return () => current++;
    };
    const getNextRoom = createRoomIndexGenerator();

    switch (type) {
      case CRUISE_TYPE:
        item.cabinInfos.forEach((cabin: any, index: number) => {
          const occupancy = initOccupancyCabin(cabin);
          const cabinInfo = handleOccupancyInfoCabin(occupancy, data, index, type, getNextRoom);
          const startRow = occupancyTemp.length + offset + 2;
          occupancyTemp.push(...cabinInfo);
          const endRow = occupancyTemp.length + offset + 1;
          mergeRules.push(...handleMergeRule(cabinInfo, header, startRow, endRow, type, isManagement));
        });
        break;
      case AIR_TYPE:
        occupancyTemp.push(...getAirPaxListDataXlsx(item[keyDetail], item.retDate).data);
        mergeRules.push(...getAirPaxListDataXlsx(item[keyDetail], item.retDate).mergeRulesData);
        break;

      default:
        item.rooms.forEach((room: any, index: number) => {
          const occupancy = initOccupancy(room);
          const roomData = handleOccupancyInfo(occupancy, data, room, index, type, isManagement, getNextRoom);

          const startRow = occupancyTemp.length + offset + 2;
          occupancyTemp.push(...roomData);
          const endRow = occupancyTemp.length + offset + 1;
          mergeRules.push(...handleMergeRule(roomData, header, startRow, endRow, type, isManagement));
        });
        break;
    }

    return { occupancyTemp, hotelMergeRules: mergeRules };
  };

  const handleDataExport = (callback: any, data: any, isManagement?: boolean, headerLead?: string, type = PACKAGE_TYPE, keyDetail = 'hotels') => {
    const dataToExport = data.map((option: any, index: number) => {
      const { data, mergeRules } = formatLayoutExport(option, type, keyDetail, headerLead, isManagement);
      return {
        name: `${i18n.global.t('common.option')} ${index + 1}`,
        data,
        mergeRules
      };
    });

    callback(dataToExport);
  };

  const handleFormatDetailExport = (data: any, headerLead?: string, type = PACKAGE_TYPE, keyDetail = 'hotels', name?: string, isManagement?: boolean) => {
    const dataToExport = data.map((option: any) => {
      const { data, mergeRules } = formatLayoutExport(option, type, keyDetail, headerLead, isManagement);
      return {
        name: name,
        data,
        mergeRules
      };
    });
    return dataToExport;
  };

  const findFirstFlight = (data: any) => {
    return data.find((element: any) => element.type === 'Outbound');
  };

  const findLastFlight = (data: any) => {
    return data.findLast((element: any) => element.type === 'Outbound');
  };

  const handleExportCruiseBooking = (callback: any, data: any, isManagement?: boolean, headerLead?: string) => {
    const dataToExport = data.map((option: any) => {
      const optionFlight = option.flights.length ? [{ ...option, gateCode: findFirstFlight(option.flights)?.gateCode, destCode: findLastFlight(option.flights)?.destCode, depDate: findFirstFlight(option.flights)?.depDate }] : [];
      const cabinSheet = handleFormatDetailExport([option], headerLead, CRUISE_TYPE, undefined, 'CRUISE', true);
      const preHotelSheet = handleFormatDetailExport(option.preHotel, headerLead, HOTEL_TYPE, 'hotels', 'PRE_HOTEL', isManagement);
      const postHotelSheet = handleFormatDetailExport(option.postHotel, headerLead, HOTEL_TYPE, 'hotels', 'POST_HOTEL', isManagement);
      const flightSheet = handleFormatDetailExport(optionFlight, headerLead, AIR_TYPE, 'flightPrices', 'FLIGHT', isManagement);
      return [...cabinSheet, ...preHotelSheet, ...postHotelSheet, ...flightSheet];
    });

    callback(dataToExport);
  };

  const convertRoomRequestsToString = (roomRequests: any) => {
    const configList = roomRequestSelections?.filter((item: any) => roomRequests?.includes(item.code));
    return configList?.reduce((prev: any, cur: any) => {
      if (!prev) {
        return cur.name;
      }
      return prev + ', ' + cur.name;
    }, '');
  };

  const validateBookingHotel = (hotels: any, minConfig: any) => {
    const { checkMinRoom } = useCheckMaxPassengersComposable();
    return hotels.some((hotel: any) => {
      const listRoom: any = [];
      hotel.rooms.forEach((room: any) => {
        listRoom.push(room.roomInfo);
      });
      return checkMinRoom(listRoom, minConfig.minPax, minConfig.minRoomsCabins);
    });
  };

  const extractRoomsAndOccupancy = (packages: any[]) => {
    const listRoom: any[] = [];
    const listOccupancy: any[] = [];

    packages.forEach((packageData: any) => {
      extractFromPackage(packageData, listRoom, listOccupancy);
    });

    return { listRoom, listOccupancy };
  };

  const extractTourRoomsAndOccupancy = (packages: any[]) => {
    const listRoom: any[] = [];
    const listOccupancy: any[] = [];

    packages.forEach((packageData: any) => {
      extractFromTour(packageData, listRoom, listOccupancy);
    });

    return { listRoom, listOccupancy };
  };

  const extractFromPackage = (packageData: any, listRoom: any[], listOccupancy: any[]) => {
    packageData?.hotels?.forEach((hotel: any) => {
      extractFromHotel(hotel, listRoom, listOccupancy);
    });
  };

  const extractFromTour = (tourData: any, listRoom: any[], listOccupancy: any[]) => {
    tourData?.tours?.forEach((room: any) => {
      extractFromHotel(room, listRoom, listOccupancy);
    });
  };

  const extractFromHotel = (hotel: any, listRoom: any[], listOccupancy: any[]) => {
    hotel.rooms?.forEach((room: any) => {
      listRoom.push(room.roomInfo);
      listOccupancy.push(room.detailedOccupancy);
    });
  };

  const validateBookingPackage = (packages: any, minConfig: any) => {
    const { checkMinRoomTotal } = useCheckMaxPassengersComposable();
    const { listRoom, listOccupancy } = extractRoomsAndOccupancy(packages);

    return checkMinRoomTotal(listRoom, listOccupancy, minConfig.minPax, minConfig.minRoomsCabins);
  };

  const validateBookingHotelOptions = (hotels: any, minConfig: any) => {
    const { checkMinRoomTotal } = useCheckMaxPassengersComposable();
    const { listRoom, listOccupancy } = extractRoomsAndOccupancy(hotels);

    return checkMinRoomTotal(listRoom, listOccupancy, minConfig.minPax, minConfig.minRoomsCabins);
  };

  const validateBookingHotels = (hotels: any, minConfig: any) => {
    return hotels.some((hotelData: any) => {
      return validateBookingHotel(hotelData?.hotels, minConfig);
    });
  };

  const isInvalidUpdate = (options: any[], editIndex: number, updatedOption: any[], minConfig: any, isHotel: boolean): boolean => {
    const newOptions = cloneDeep(options);
    newOptions[editIndex] = updatedOption;
    const totalPassengers = newOptions.reduce((sum, opt) => sum + (opt.adtNo ?? 0) + (opt.chdNo ?? 0), 0);
    const totalPassengersHotel = newOptions.reduce((sum, opt) => sum + (opt.adt ?? 0) + (opt.chd ?? 0), 0);
    const totalRooms = newOptions.reduce((sum, opt) => sum + (opt.numOfRooms ?? 0), 0);
    return (isHotel ? totalPassengersHotel : totalPassengers) < minConfig.minPax || totalRooms < minConfig.minRoomsCabins;
  };

  const isInvalidTourUpdate = (options: any[], editIndex: number, tourIndex: number, updatedOption: any[], minConfig: any): boolean => {
    const newOptions = cloneDeep(options);
    newOptions[editIndex].tours[tourIndex] = updatedOption;
    let totalPassengers = 0;
    let totalRooms = 0;
    newOptions.forEach((option: any) => {
      totalPassengers += option.tours.reduce((sum: number, opt: any) => sum + (opt.adtNo ?? 0) + (opt.chdNo ?? 0), 0);
      totalRooms += option.tours.reduce((sum: number, opt: any) => sum + (opt.numOfRooms ?? 0), 0);
    });
    return totalPassengers < minConfig.minPax || totalRooms < minConfig.minRoomsCabins;
  };

  const isInvalidCruiseUpdate = (options: any[], editIndex: number, updatedOption: any[], minConfig: any): boolean => {
    const newOptions = [...options];
    newOptions[editIndex] = updatedOption;
    const totalPassengersCruise = newOptions.reduce((sum, opt) => sum + (opt.adt ?? 0) + (opt.chd ?? 0), 0);
    const totalRooms = newOptions.reduce((sum, opt) => sum + (opt.cabinNo ?? 0), 0);
    return totalPassengersCruise < minConfig.minPax || totalRooms < minConfig.minRoomsCabins;
  };

  const canUpdateAirPassenger = (options: any[], editIndex: number, updatedOption: any[], minConfig: any): boolean => {
    const newOptions = [...options];
    newOptions[editIndex] = updatedOption;
    const totalPassengers = newOptions.reduce((sum, opt) => sum + (opt.adtNo ?? 0) + (opt.chdNo ?? 0), 0);
    const totalPassengersBusiness = newOptions.reduce((sum, opt) => sum + (opt.businessAdtNo ?? 0) + (opt.businessChdNo ?? 0), 0);
    return totalPassengers + totalPassengersBusiness < minConfig.minPax;
  };

  const validateBookingMdt = (multiDayTour: any, minConfig: any) => {
    const { checkMinRoomTotal } = useCheckMaxPassengersComposable();
    const { listRoom, listOccupancy } = extractTourRoomsAndOccupancy(multiDayTour);
    return checkMinRoomTotal(listRoom, listOccupancy, minConfig.minPax, minConfig.minRoomsCabins);
  };

  const validateBookingFlight = (airs: any, minConfig: any) => {
    let total = 0;
    airs.forEach((air: any) => {
      air.price.map((item: any) => {
        total += (item.adtNo ?? 0) + (item.chdNo ?? 0);
      });
    });
    return total < minConfig.minPax;
  };

  const validateBookingCruise = (cruises: any, minConfig: any) => {
    const { checkMinCabin } = useCheckMaxPassengersComposable();

    const validateCruise = checkMinCabin(cruises, minConfig.minRoomsCabins, minConfig.minPax);
    return validateCruise;
  };

  const initDetailedOccupancy = () => {
    return {
      singleRoom: null,
      doubleRoom: null,
      tripleRoom: null,
      quadRoom: null,
      fiveRoom: null,
      sixRoom: null
    };
  };

  const calculateRoomTotals = (data: any) => {
    const result: any = {};
    for (const roomType in data) {
      const rooms = data[roomType];
      result[roomType] = rooms.reduce(
        (acc: any, room: any) => {
          acc.children += room.children || 0;
          acc.infants += room.infants || 0;
          return acc;
        },
        { children: 0, infants: 0 }
      );
    }
    return result;
  };

  const handleTotalAdultsChildren = (occupancy: any) => {
    let totalAdt = 0;
    let totalChild = 0;
    for (const roomType in occupancy) {
      if (occupancy[roomType] && roomType.endsWith('Room')) {
        occupancy[roomType].forEach((person: any) => {
          totalAdt += person.adults;
          totalChild += person.children;
        });
      }
    }
    return totalAdt + totalChild;
  };

  const handleChildrenTotal = (occupancyTemp: any, passengerInfo: any, childrenTotal: any, index: number, indexRoomType: number) => {
    for (let i = 0; i < childrenTotal; i++) {
      if (occupancyTemp.value[index].passengers[indexRoomType].children.length < childrenTotal) {
        occupancyTemp.value[index].passengers[indexRoomType].children = [
          ...occupancyTemp.value[index].passengers[indexRoomType].children,
          { ...cloneDeep(passengerInfo), key: `children${index}${indexRoomType}${occupancyTemp.value[index].passengers[indexRoomType].children.length}` }
        ];
      }
      if (occupancyTemp.value[index].passengers[indexRoomType].children.length > childrenTotal) {
        occupancyTemp.value[index].passengers[indexRoomType].children = occupancyTemp.value[index].passengers[indexRoomType].children.slice(0, childrenTotal);
      }
    }
  };

  const handleInfantsTotal = (occupancyTemp: any, passengerInfo: any, infantsTotal: any, index: number, indexRoomType: number) => {
    for (let i = 0; i < infantsTotal; i++) {
      if (occupancyTemp.value[index].passengers[indexRoomType].infants.length < infantsTotal) {
        occupancyTemp.value[index].passengers[indexRoomType].infants = [
          ...occupancyTemp.value[index].passengers[indexRoomType].infants,
          { ...cloneDeep(passengerInfo), key: `infants${index}${indexRoomType}${occupancyTemp.value[index].passengers[indexRoomType].infants.length}` }
        ];
      }
      if (occupancyTemp.value[index].passengers[indexRoomType].infants.length > infantsTotal) {
        occupancyTemp.value[index].passengers[indexRoomType].infants = occupancyTemp.value[index].passengers[indexRoomType].infants.slice(0, infantsTotal);
      }
    }
  };

  const handleRenderChild = (room: any, occupancyTemp: any, passengerInfo: any) => {
    const roomTemp = room;
    if (roomTemp?.detailedOccupancy) {
      const filterArrays = Object.values(roomTemp.detailedOccupancy).filter((item: any) => Array.isArray(item));
      filterArrays.forEach((roomTypes: any, index: number) => {
        if (roomTypes) {
          roomTypes.forEach((roomType: any, indexRoomType: number) => {
            const childrenTotal = roomType.children;
            const infantsTotal = roomType.infants;
            if (childrenTotal) {
              handleChildrenTotal(occupancyTemp, passengerInfo, childrenTotal, index, indexRoomType);
            } else {
              occupancyTemp.value[index].passengers[indexRoomType].children = [];
            }
            if (infantsTotal) {
              handleInfantsTotal(occupancyTemp, passengerInfo, infantsTotal, index, indexRoomType);
            } else {
              occupancyTemp.value[index].passengers[indexRoomType].infants = [];
            }
          });
        }
      });
    }
  };

  const areAllRoomInfosNull = (room: any, isCruise: boolean = false) => {
    const roomInfo = isCruise ? room : room.roomInfo;
    const roomInfoFields = [roomInfo.singleRoomInfos, roomInfo.doubleRoomInfos, roomInfo.tripleRoomInfos, roomInfo.quadRoomInfos, roomInfo.fiveInRoomInfos, roomInfo.sixInRoomInfos];
    return roomInfoFields.every((field) => field === null);
  };

  const checkCabinCode = (cabinCode: any) => {
    switch (cabinCode) {
      case 'OS':
        return i18n.global.t('worksheet.outside');
      case 'BA':
        return i18n.global.t('worksheet.balcony');
      case 'SU':
        return i18n.global.t('worksheet.suite');
      default:
        return i18n.global.t('worksheet.inside');
    }
  };

  const renderFileNameExport = (quoteId: string, groupName: string, type?: string) => {
    if (type === CRUISE_TYPE) {
      return `${quoteId} - ${groupName} - ${i18n.global.t('common.option')} %i - ${i18n.global.t('common.labels.passenger_manifest')}.xlsx`;
    } else {
      return `${quoteId} - ${groupName} - ${i18n.global.t('common.labels.passenger_manifest')}.xlsx`;
    }
  };

  return {
    checkInputAlphanumeric,
    checkInputAlpha,
    findGroupType,
    checkInputEmail,
    getAge,
    checkPriorityStatus,
    roomFields,
    hasEditOccupacyPermission,
    hasViewOccupacyPermission,
    hasEditBookPermission,
    columnStyles,
    validateBookingHotel,
    validateBookingFlight,
    validateBookingCruise,
    validateBookingPackage,
    validateBookingMdt,
    roomRequestSelections,
    convertRoomRequestsToString,
    initDetailedOccupancy,
    calculateRoomTotals,
    handleTotalAdultsChildren,
    handleRenderChild,
    areAllRoomInfosNull,
    handleDataExport,
    checkCabinCode,
    validateBookingHotels,
    validateBookingHotelOptions,
    handleExportCruiseBooking,
    renderFileNameExport,
    isInvalidUpdate,
    isInvalidCruiseUpdate,
    isInvalidTourUpdate,
    canUpdateAirPassenger,
    extractRoomsAndOccupancy,
    extractTourRoomsAndOccupancy,
    extractFromPackage,
    extractFromHotel,
    extractFromTour
  };
}
